import { supabase, type UserSubscription } from '../lib/supabase';
import { normalizeTier, type CanonicalTier } from '../types/tiers';

export type TrustedSubscription = {
  tier: CanonicalTier;
  status: string;
  expiresAt: number | null;
  cancelAtPeriodEnd: boolean;
  isActive: boolean;
};

/**
 * Derive UI entitlement from a trusted Supabase row (read-only).
 */
export function computeTrustedSubscription(sub: UserSubscription): TrustedSubscription {
  const tier = normalizeTier(sub.tier);
  const expiresAt = sub.expires_at ? new Date(sub.expires_at).getTime() : null;
  const status = sub.status || 'none';
  const cancelAtPeriodEnd = Boolean(sub.cancel_at_period_end);
  const withinExpiry = !expiresAt || expiresAt > Date.now();

  let isActive = false;
  if (tier !== 'free' && withinExpiry) {
    if (
      status === 'trialing' ||
      status === 'active' ||
      status === 'past_due' ||
      status === 'canceled' ||
      status === 'none'
    ) {
      isActive = true;
    }
  }

  return {
    tier: isActive ? tier : 'free',
    status,
    expiresAt,
    cancelAtPeriodEnd,
    isActive,
  };
}

/**
 * Read-only fetch of the signed-in user's personal subscription.
 * Never inserts or updates paid entitlement fields.
 */
export async function fetchOwnSubscription(userId: string): Promise<TrustedSubscription | null> {
  const { data, error } = await supabase
    .from('subscriptions')
    .select(
      'id, user_id, tier, status, expires_at, cancel_at_period_end, created_at, updated_at'
    )
    .eq('user_id', userId)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching subscription:', error);
    throw error;
  }

  if (!data) return null;
  return computeTrustedSubscription(data as UserSubscription);
}
